import { useEffect, useState, useCallback } from "react";
import {
  Server,
  RefreshCcw,
  CheckCircle2,
  AlertCircle,
  Clock,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { getFtpStatus, ftpSyncNow, formatDateTime } from "@/lib/ocorenApi";

const Stat = ({ label, value, tone = "default" }) => {
  const tones = {
    default: "text-[#09090B]",
    success: "text-[#10B981]",
    warning: "text-[#F59E0B]",
    error: "text-[#F43F5E]",
    muted: "text-[#52525B]",
  };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-[#52525B] font-bold">
        {label}
      </div>
      <div className={`text-sm font-mono-data mt-0.5 ${tones[tone]}`}>
        {value || "—"}
      </div>
    </div>
  );
};

export const FtpPanel = ({ onSynced }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const s = await getFtpStatus();
      setStatus(s);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 15000); // atualiza a cada 15s
    return () => clearInterval(t);
  }, [refresh]);

  const onSync = async () => {
    setSyncing(true);
    toast.message("Sincronizando com o FTP…", {
      description: "Pode levar alguns segundos.",
    });
    try {
      const res = await ftpSyncNow();
      if (res?.skipped) {
        toast.info("Sincronização já em andamento");
      } else if (res?.error) {
        toast.error("Falha na sincronização", { description: res.error });
      } else {
        const parts = [];
        if (res.novos_processados > 0) parts.push(`${res.novos_processados} novo(s)`);
        if (res.nfs_novas > 0) parts.push(`${res.nfs_novas} NF(s) nova(s)`);
        if (res.status_alterados > 0) parts.push(`${res.status_alterados} status alterado(s)`);
        if (res.erros > 0) parts.push(`${res.erros} erro(s)`);
        toast.success(
          parts.length ? parts.join(" • ") : "Nada novo no FTP",
          { description: `${res.total_remote} arquivos remotos • ${res.ja_existentes} já existentes` }
        );
        onSynced?.(res);
      }
      await refresh();
    } catch (e) {
      toast.error("Falha ao sincronizar");
    } finally {
      setSyncing(false);
    }
  };

  if (loading || !status)
    return (
      <div className="bg-white border border-[#E4E4E7] rounded-md p-5 mb-6">
        <div className="text-sm text-[#52525B]">Carregando status FTP…</div>
      </div>
    );

  const lastSummary = status.last_summary;
  const hasError = !!status.last_error;
  const isRunning = !!status.running;

  return (
    <section
      data-testid="ftp-panel"
      className="bg-white border border-[#E4E4E7] rounded-md p-5 mb-6"
    >
      <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-md flex items-center justify-center ${
              hasError
                ? "bg-rose-50 border border-rose-200"
                : status.configured
                ? "bg-emerald-50 border border-emerald-200"
                : "bg-zinc-100 border border-zinc-200"
            }`}
          >
            <Server
              size={18}
              className={
                hasError
                  ? "text-rose-700"
                  : status.configured
                  ? "text-emerald-700"
                  : "text-zinc-500"
              }
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#52525B] font-bold">
              Sincronização Automática
            </div>
            <div className="font-heading text-lg font-semibold text-[#09090B] mt-0.5">
              FTP — {status.host || "não configurado"}
            </div>
            <div className="text-[11px] text-[#52525B] mt-0.5 font-mono-data">
              Intervalo: a cada {status.interval_minutes} min
            </div>
          </div>
        </div>

        <button
          data-testid="ftp-sync-now-btn"
          onClick={onSync}
          disabled={syncing || isRunning || !status.configured}
          className="bg-[#09090B] text-white hover:bg-[#27272A] disabled:opacity-60 px-4 py-2 text-sm font-medium rounded-md flex items-center gap-2 transition-colors"
        >
          {syncing || isRunning ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Sincronizando…
            </>
          ) : (
            <>
              <RefreshCcw size={14} />
              Sincronizar agora
            </>
          )}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-[#E4E4E7]">
        <Stat
          label="Última execução"
          value={formatDateTime(status.last_run_at)}
          tone="muted"
        />
        <Stat
          label="Última com sucesso"
          value={formatDateTime(status.last_success_at)}
          tone={hasError ? "muted" : "success"}
        />
        <Stat
          label="Próxima execução"
          value={formatDateTime(status.next_run_at)}
          tone="muted"
        />
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-[#52525B] font-bold">
            Estado
          </div>
          <div className="text-sm mt-0.5 flex items-center gap-1.5">
            {hasError ? (
              <>
                <AlertCircle size={13} className="text-rose-700" />
                <span className="text-rose-700 font-semibold">Erro</span>
              </>
            ) : isRunning ? (
              <>
                <Loader2 size={13} className="text-blue-700 animate-spin" />
                <span className="text-blue-700 font-semibold">Executando</span>
              </>
            ) : status.last_success_at ? (
              <>
                <CheckCircle2 size={13} className="text-emerald-700" />
                <span className="text-emerald-700 font-semibold">Conectado</span>
              </>
            ) : (
              <>
                <Clock size={13} className="text-[#52525B]" />
                <span className="text-[#52525B]">Aguardando 1ª execução</span>
              </>
            )}
          </div>
        </div>
      </div>

      {hasError && (
        <div
          data-testid="ftp-error"
          className="mt-4 p-3 bg-rose-50 border border-rose-200 rounded-md text-xs text-rose-900"
        >
          <strong>Último erro:</strong> {status.last_error}
        </div>
      )}

      {lastSummary && !hasError && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-[#FAFAFA] border border-[#E4E4E7] rounded-md px-3 py-2">
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#52525B] font-bold">
              Arquivos no FTP
            </div>
            <div className="text-lg font-mono-data font-bold text-[#09090B]">
              {lastSummary.total_remote}
            </div>
          </div>
          <div className="bg-[#FAFAFA] border border-[#E4E4E7] rounded-md px-3 py-2">
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#52525B] font-bold">
              Novos processados
            </div>
            <div className="text-lg font-mono-data font-bold text-emerald-700">
              {lastSummary.novos_processados}
            </div>
          </div>
          <div className="bg-[#FAFAFA] border border-[#E4E4E7] rounded-md px-3 py-2">
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#52525B] font-bold">
              Já existentes
            </div>
            <div className="text-lg font-mono-data font-bold text-[#52525B]">
              {lastSummary.ja_existentes}
            </div>
          </div>
          <div className="bg-[#FAFAFA] border border-[#E4E4E7] rounded-md px-3 py-2">
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#52525B] font-bold">
              NFs novas
            </div>
            <div className="text-lg font-mono-data font-bold text-blue-700">
              {lastSummary.nfs_novas}
            </div>
          </div>
          <div className="bg-[#FAFAFA] border border-[#E4E4E7] rounded-md px-3 py-2">
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#52525B] font-bold">
              Erros
            </div>
            <div
              className={`text-lg font-mono-data font-bold ${
                lastSummary.erros > 0 ? "text-rose-700" : "text-[#52525B]"
              }`}
            >
              {lastSummary.erros}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default FtpPanel;
